import clsx from 'clsx'
import React, {useCallback, useMemo} from 'react'

import {GeolocationIcon, IconButton, Tooltip, useTransition} from '../../../'
import {useMapContextValue} from '../../context'
import {LngLat, MapGeolocationControlProps} from '../../types'
import {MapMarker} from '../../../mapmarker'
import {classes} from './MapGeolocationCustomControl.classes'
import {getGeolocationFromBrowser} from './MapGeolocationCustomControl.utils'
import MapIcon from './MapIcon'

type OnGeolocatePositionType = MapGeolocationControlProps['onGeolocatePosition']

type Props = {
  classes?: {
    root?: string
    tooltip?: string
  }
  initialLocation?: LngLat
  /**
   * ask location on mount
   */
  initializeLocation?: boolean
  onGeolocatePosition?: OnGeolocatePositionType
  translations?: {
    geolocationError?: string
  }
}

const GEOLOCATION_KEY = 'GEOLOCATION_KEY'
const DEFAULT_TRANSLATIONS: Props['translations'] = {
  geolocationError: 'Geolocation is not available',
}

type GeoPositionType = {
  coords: LngLat
  accuracy?: number
}

function MapGeolocationCustomControl({
  initialLocation,
  initializeLocation,
  onGeolocatePosition,
  translations = {},
  ...props
}: Props) {
  const mapContext = useMapContextValue()
  const t = useMemo(() => ({...DEFAULT_TRANSLATIONS, ...translations}), [translations])
  const [{isPending: isLoading, error, payload: location = initialLocation}, startTransition] = useTransition<
    GeoPositionType['coords'],
    boolean
  >(
    initializeLocation
      ? () =>
          getGeolocationFromBrowser()
            .then(lngLat => {
              onGeolocatePosition?.(lngLat)
              return lngLat
            })
            .catch(e => {
              if (initialLocation) {
                onGeolocatePosition?.(initialLocation)
              }
              throw e
            })
      : undefined,
    {
      key: GEOLOCATION_KEY,
      shouldShowBaseError: false
    },
  )
  const iconName = mapContext?.lang === 'ru_RU' ? 'ya-ru' : 'ya-en'

  const clickHandler = useCallback(() => {
    return startTransition(getGeolocationFromBrowser, {
      onCommit: lngLat => {
        onGeolocatePosition?.(lngLat)
      },
      onError: () => {
        if (onGeolocatePosition && initialLocation) {
          onGeolocatePosition(initialLocation)
        }
      },
    })
  }, [initialLocation, onGeolocatePosition, startTransition])

  return (
    <>
      <Tooltip
        className={clsx(classes.tooltip, props.classes?.tooltip)}
        offset={[0, 1]}
        opened={!!error}
        placement="left"
        title={t.geolocationError}
      >
        <span>
          <IconButton
            className={clsx(classes.container, props.classes?.root)}
            loading={isLoading}
            onPress={clickHandler}
            shape="circle"
            variant="floating"
          >
            <GeolocationIcon />
          </IconButton>
        </span>
      </Tooltip>
      {!!location && (
        <MapMarker coordinates={location} zIndex={-1}>
          <div className={classes.iconWrap}>
            <MapIcon icon={iconName} />
          </div>
        </MapMarker>
      )}
    </>
  )
}

export default React.memo(MapGeolocationCustomControl)
